import { Box, Button, Divider, useToast } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { ApiMessageStack, FormControlLabel, FormControlV2 } from "components";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { REQUIRED_FIELD } from "constants/validator-messages";
import { AccountIntegrationTypeEnum } from "enums/account-integration-type.enum";
import { FormControlsTypeEnum } from "enums/form-controls-type.enum";
import { observer } from "mobx-react";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { AccountIntegrationsStore } from "store/UserAccounts/UserAccount/AccountIntegrations/AccountIntegrations.store";
import { TAutomatedReferralsConfig } from "types/account-integration.type";
import * as Yup from "yup";

type TFieldValues = {
  referralSystemId: number | undefined;
  sendOutOfAreaLeads: boolean;
};

const initialValues: TFieldValues = {
  referralSystemId: undefined,
  sendOutOfAreaLeads: true,
};

const validationSchema = Yup.object().shape({
  referralSystemId: Yup.number().required(REQUIRED_FIELD),
  sendOutOfAreaLeads: Yup.boolean().required(REQUIRED_FIELD),
});

interface IProps {
  closePrompt: () => void;
  accountIntegrationsStore: AccountIntegrationsStore;
}

export const ConnectAutomatedReferralsForm: React.FC<IProps> = observer(
  ({ closePrompt, accountIntegrationsStore }) => {
    const toast = useToast();

    const {
      handleSubmit,
      control,
      formState: { isSubmitting },
    } = useForm<TFieldValues>({
      defaultValues: initialValues,
      mode: "onSubmit",
      resolver: yupResolver(validationSchema),
    });

    const onSubmit = useCallback(
      async ({ referralSystemId, sendOutOfAreaLeads }: TFieldValues) => {
        try {
          if (!referralSystemId) {
            throw new Error("Referral system id required.");
          }
          await accountIntegrationsStore.createAccountIntegration<
            TAutomatedReferralsConfig
          >({
            type: AccountIntegrationTypeEnum.AUTOMATED_REFERRALS,
            active: true,
            integrationConfig: { referralSystemId, sendOutOfAreaLeads },
          });

          toast({
            ...DEFAULT_SUCCESS_TOAST_OPTIONS,
            description: (
              <ApiMessageStack
                messageStack={"Automated Referrals connected successfully"}
              />
            ),
          });
          closePrompt();
        } catch (error) {
          toast({
            ...DEFAULT_ERROR_TOAST_OPTIONS,
            description: <ApiMessageStack messageStack={error.message} />,
          });
        }
      },
      [accountIntegrationsStore, closePrompt, toast]
    );

    return (
      <Box pt={5}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box mb={5}>
            <FormControlV2<TFieldValues>
              name={"referralSystemId"}
              control={control}
              label={"Referral system id"}
              type={FormControlsTypeEnum.NUMBER}
              additionalProps={{
                placeholder: "Referral system id",
              }}
            />
          </Box>
          <Box mb={5}>
            <FormControlLabel>Send out of area leads</FormControlLabel>
            <FormControlV2<TFieldValues>
              name={"sendOutOfAreaLeads"}
              control={control}
              type={FormControlsTypeEnum.SWITCH}
            />
          </Box>
          <Divider mb={4} />
          <Box
            width={"100%"}
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"flex-end"}
          >
            <Button
              type={"submit"}
              colorScheme={"blue"}
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Connect
            </Button>
          </Box>
        </form>
      </Box>
    );
  }
);
